import React, { useState, useEffect } from 'react';
import {
  pollsAdminService,
  PollWithDetails,
  CreatePollData,
  CreatePollOptionData,
} from '../../services/polls.admin.service';
import { seasonsService, SeasonWithReality } from '../../services/seasons.service';
import { contestantsService, ContestantWithSeason } from '../../services/contestants.service';
import { realitiesService, Reality } from '../../services/realities.service';

type VoteMode = 'single' | 'points';

export function PollsPage() {
  const [polls, setPolls] = useState<PollWithDetails[]>([]);
  const [seasons, setSeasons] = useState<SeasonWithReality[]>([]);
  const [realities, setRealities] = useState<Reality[]>([]);
  const [contestants, setContestants] = useState<ContestantWithSeason[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPoll, setEditingPoll] = useState<PollWithDetails | null>(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{ isOpen: boolean; pollId: string | null; pollTitle: string }>({
    isOpen: false,
    pollId: null,
    pollTitle: '',
  });
  const [betrayalModal, setBetrayalModal] = useState<{
    isOpen: boolean;
    poll: PollWithDetails | null;
    selectedOptionId: string;
    newContestantId: string;
    reason: string;
    isSubmitting: boolean;
  }>({
    isOpen: false,
    poll: null,
    selectedOptionId: '',
    newContestantId: '',
    reason: '',
    isSubmitting: false,
  });

  const [savedModal, setSavedModal] = useState<{
    isOpen: boolean;
    poll: PollWithDetails | null;
    selectedOptionId: string;
    isSubmitting: boolean;
  }>({
    isOpen: false,
    poll: null,
    selectedOptionId: '',
    isSubmitting: false,
  });

  const [duplicationModal, setDuplicationModal] = useState<{
    isOpen: boolean;
    poll: PollWithDetails | null;
    sourceOptionId: string;
    targetContestantId: string;
    reason: string;
    isSubmitting: boolean;
  }>({
    isOpen: false,
    poll: null,
    sourceOptionId: '',
    targetContestantId: '',
    reason: '',
    isSubmitting: false,
  });

  // Form state
  const [formData, setFormData] = useState<CreatePollData>({
    title: '',
    description: '',
    season_id: '',
    poll_mode: 'manual',
    start_date: '',
    end_date: '',
    show_results: true,
    status: 'active',
  });

  const [modalRealityId, setModalRealityId] = useState<string>('');
  const [voteMode, setVoteMode] = useState<VoteMode>('single');
  const [pointsToDistribute, setPointsToDistribute] = useState(10);
  const [dailyReset, setDailyReset] = useState(false);
  const [enableTimer, setEnableTimer] = useState(false);
  const [selectedContestants, setSelectedContestants] = useState<string[]>([]);
  const [customOptions, setCustomOptions] = useState<string[]>(['', '']);
  const [useContestants, setUseContestants] = useState(true);

  useEffect(() => {
    loadRealities();
    loadSeasons();
    loadPolls();
  }, []);

  useEffect(() => {
    loadPolls();
  }, [selectedSeason]);

  useEffect(() => {
    if (formData.season_id) {
      loadContestants(formData.season_id);
    }
  }, [formData.season_id]);

  useEffect(() => {
    if (betrayalModal.poll?.season_id) {
      loadContestants(betrayalModal.poll.season_id);
    }
  }, [betrayalModal.poll]);

  useEffect(() => {
    if (duplicationModal.poll?.season_id) {
      loadContestants(duplicationModal.poll.season_id);
    }
  }, [duplicationModal.poll]);

  const loadRealities = async () => {
    try {
      const data = await realitiesService.getAll();
      setRealities(data.filter((r) => r.active));
    } catch (err: any) {
      setError(err.message || 'Error al cargar los realities');
    }
  };

  const loadSeasons = async () => {
    try {
      const data = await seasonsService.getAll();
      setSeasons(data.filter((s) => s.active));
    } catch (err: any) {
      setError(err.message || 'Error al cargar las temporadas');
    }
  };

  const loadPolls = async () => {
    try {
      setLoading(true);
      let data;
      if (selectedSeason === 'all') {
        data = await pollsAdminService.getAll();
      } else {
        data = await pollsAdminService.getBySeason(selectedSeason);
      }
      setPolls(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar las encuestas');
    } finally {
      setLoading(false);
    }
  };

  const loadContestants = async (seasonId: string) => {
    try {
      const data = await contestantsService.getBySeason(seasonId);
      setContestants(data.filter((c) => c.status === 'active' || c.status === 'eliminated'));
    } catch (err: any) {
    }
  };

  const handleOpenModal = (poll?: PollWithDetails) => {
    if (poll) {
      setEditingPoll(poll);

      // Convertir fechas de formato ISO a datetime-local (YYYY-MM-DDTHH:mm)
      const formatDateTimeLocal = (isoDate: string | null) => {
        if (!isoDate) return '';
        return new Date(isoDate).toISOString().slice(0, 16);
      };

      setFormData({
        title: poll.title,
        description: poll.description || '',
        season_id: poll.season_id,
        poll_mode: poll.poll_mode || 'manual',
        start_date: formatDateTimeLocal(poll.start_date),
        end_date: formatDateTimeLocal(poll.end_date),
        show_results: poll.show_results ?? true,
        status: poll.status || 'draft',
      });

      // Set the reality_id from the season
      const season = seasons.find(s => s.id === poll.season_id);
      setModalRealityId(season?.reality_id || '');

      // Detect vote mode
      if (poll.poll_points_config?.max_points_per_user && poll.poll_points_config.max_points_per_user > 1) {
        setVoteMode('points');
        setPointsToDistribute(poll.poll_points_config.max_points_per_user);
        setDailyReset(poll.poll_points_config.daily_reset || false);
      } else {
        setVoteMode('single');
      }

      setEnableTimer(!!poll.end_date);

      // Load options
      const contestantIds = poll.poll_options
        .filter((opt) => opt.contestant_id)
        .map((opt) => opt.contestant_id!);
      const textOptions = poll.poll_options
        .filter((opt) => opt.text && !opt.contestant_id)
        .map((opt) => opt.text!);

      if (contestantIds.length > 0) {
        setUseContestants(true);
        setSelectedContestants(contestantIds);
      } else {
        setUseContestants(false);
        setCustomOptions(textOptions.length > 0 ? textOptions : ['', '']);
      }
    } else {
      setEditingPoll(null);

      // Determine initial reality and season
      let initialSeasonId = '';
      let initialRealityId = '';

      if (selectedSeason !== 'all') {
        initialSeasonId = selectedSeason;
        const season = seasons.find(s => s.id === selectedSeason);
        initialRealityId = season?.reality_id || '';
      }

      setModalRealityId(initialRealityId);
      setFormData({
        title: '',
        description: '',
        season_id: initialSeasonId,
        poll_mode: 'manual',
        start_date: '',
        end_date: '',
        show_results: true,
        status: 'active',
      });
      setVoteMode('single');
      setPointsToDistribute(10);
      setDailyReset(false);
      setEnableTimer(false);
      setSelectedContestants([]);
      setCustomOptions(['', '']);
      setUseContestants(true);
    }
    setIsModalOpen(true);
  };

  // Filtrar temporadas según el reality seleccionado en el modal
  const getModalSeasons = () => {
    if (!modalRealityId) {
      return [];
    }
    return seasons.filter(s => s.reality_id === modalRealityId);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPoll(null);
    setModalRealityId('');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validations
    const options: CreatePollOptionData[] = [];

    if (useContestants) {
      if (selectedContestants.length < 2) {
        setError('Debes seleccionar al menos 2 concursantes');
        return;
      }
      selectedContestants.forEach((contestantId, index) => {
        options.push({
          contestant_id: contestantId,
          order: index,
        });
      });
    } else {
      const validOptions = customOptions.filter((opt) => opt.trim() !== '');
      if (validOptions.length < 2) {
        setError('Debes agregar al menos 2 opciones');
        return;
      }
      validOptions.forEach((text, index) => {
        options.push({
          text,
          order: index,
        });
      });
    }

    if (enableTimer && !formData.end_date) {
      setError('Debes especificar la fecha de cierre');
      return;
    }

    // Prepare poll data based on vote mode
    const pollDataToSave: CreatePollData = {
      ...formData,
      end_date: enableTimer ? formData.end_date : '',
    };

    // Points config - needed for both modes to store daily_reset
    const pointsConfig = {
      points_per_vote: 1,
      max_points_per_user: voteMode === 'points' ? pointsToDistribute : 1,
      daily_reset: dailyReset,
    };

    try {
      if (editingPoll) {
        await pollsAdminService.update(editingPoll.id, pollDataToSave, options, pointsConfig);
      } else {
        await pollsAdminService.create(pollDataToSave, options, pointsConfig);
      }
      await loadPolls();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'Error al guardar la encuesta');
    }
  };

  const handleDelete = async (id: string, title: string) => {
    setDeleteConfirmModal({ isOpen: true, pollId: id, pollTitle: title });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmModal.pollId) return;

    try {
      await pollsAdminService.delete(deleteConfirmModal.pollId);
      await loadPolls();
      setDeleteConfirmModal({ isOpen: false, pollId: null, pollTitle: '' });
    } catch (err: any) {
      setError(err.message || 'Error al eliminar la encuesta');
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await pollsAdminService.activate(id);
      await loadPolls();
    } catch (err: any) {
      setError(err.message || 'Error al activar la encuesta');
    }
  };

  const handleClose = async (id: string) => {
    try {
      await pollsAdminService.close(id);
      await loadPolls();
    } catch (err: any) {
      setError(err.message || 'Error al cerrar la encuesta');
    }
  };

  const handleSetFeatured = async (id: string) => {
    try {
      await pollsAdminService.setFeatured(id);
      await loadPolls();
    } catch (err: any) {
      setError(err.message || 'Error al fijar la encuesta');
    }
  };

  const handleOpenBetrayalModal = (poll: PollWithDetails) => {
    setBetrayalModal({
      isOpen: true,
      poll,
      selectedOptionId: '',
      newContestantId: '',
      reason: '',
      isSubmitting: false,
    });
  };

  const handleCloseBetrayalModal = () => {
    setBetrayalModal({
      isOpen: false,
      poll: null,
      selectedOptionId: '',
      newContestantId: '',
      reason: '',
      isSubmitting: false,
    });
    setError('');
  };

  const handleBetrayalSubmit = async () => {
    if (!betrayalModal.poll || !betrayalModal.selectedOptionId || !betrayalModal.newContestantId) {
      setError('Debes seleccionar el nominado que se salva y el nuevo nominado');
      return;
    }

    setBetrayalModal(prev => ({ ...prev, isSubmitting: true }));
    setError('');

    try {
      await pollsAdminService.replaceContestant(
        betrayalModal.poll.id,
        betrayalModal.selectedOptionId,
        betrayalModal.newContestantId,
        betrayalModal.reason || undefined
      );
      await loadPolls();
      handleCloseBetrayalModal();
    } catch (err: any) {
      setError(err.message || 'Error al ejecutar la traición');
      setBetrayalModal(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  const handleOpenSavedModal = (poll: PollWithDetails) => {
    setSavedModal({
      isOpen: true,
      poll,
      selectedOptionId: '',
      isSubmitting: false,
    });
  };

  const handleCloseSavedModal = () => {
    setSavedModal({
      isOpen: false,
      poll: null,
      selectedOptionId: '',
      isSubmitting: false,
    });
    setError('');
  };

  const handleSavedSubmit = async () => {
    if (!savedModal.poll || !savedModal.selectedOptionId) {
      setError('Debes seleccionar un nominado');
      return;
    }

    setSavedModal(prev => ({ ...prev, isSubmitting: true }));
    setError('');

    try {
      const selectedOption = savedModal.poll.poll_options.find(opt => opt.id === savedModal.selectedOptionId);
      if (!selectedOption) {
        throw new Error('Opción no encontrada');
      }

      // Toggle saved status
      if (selectedOption.is_saved) {
        await pollsAdminService.removeSaved(savedModal.selectedOptionId);
      } else {
        await pollsAdminService.setSaved(savedModal.selectedOptionId);
      }

      await loadPolls();
      handleCloseSavedModal();
    } catch (err: any) {
      setError(err.message || 'Error al marcar salvado');
      setSavedModal(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  const handleOpenDuplicationModal = (poll: PollWithDetails) => {
    setDuplicationModal({
      isOpen: true,
      poll,
      sourceOptionId: '',
      targetContestantId: '',
      reason: '',
      isSubmitting: false,
    });
  };

  const handleCloseDuplicationModal = () => {
    setDuplicationModal({
      isOpen: false,
      poll: null,
      sourceOptionId: '',
      targetContestantId: '',
      reason: '',
      isSubmitting: false,
    });
    setError('');
  };

  const handleDuplicationSubmit = async () => {
    if (!duplicationModal.poll || !duplicationModal.sourceOptionId || !duplicationModal.targetContestantId) {
      setError('Debes seleccionar el candidato origen y el candidato destino');
      return;
    }

    setDuplicationModal(prev => ({ ...prev, isSubmitting: true }));
    setError('');

    try {
      await pollsAdminService.duplicateVotes(
        duplicationModal.poll.id,
        duplicationModal.sourceOptionId,
        duplicationModal.targetContestantId,
        duplicationModal.reason || undefined
      );
      await loadPolls();
      handleCloseDuplicationModal();
    } catch (err: any) {
      setError(err.message || 'Error al duplicar votos');
      setDuplicationModal(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  // Get available contestants for betrayal (not currently nominated)
  const getAvailableContestants = () => {
    if (!betrayalModal.poll) return [];

    const nominatedIds = betrayalModal.poll.poll_options
      .filter(opt => opt.contestant_id)
      .map(opt => opt.contestant_id!);

    return contestants.filter(c => !nominatedIds.includes(c.id));
  };

  // Get available contestants for duplication (not currently nominated)
  const getAvailableContestantsForDuplication = () => {
    if (!duplicationModal.poll) return [];

    const nominatedIds = duplicationModal.poll.poll_options
      .filter(opt => opt.contestant_id)
      .map(opt => opt.contestant_id!);

    return contestants.filter(c => !nominatedIds.includes(c.id));
  };

  // Get vote stats for selected option
  const getSelectedOptionStats = () => {
    if (!betrayalModal.poll || !betrayalModal.selectedOptionId) return null;

    const option = betrayalModal.poll.poll_options.find(
      opt => opt.id === betrayalModal.selectedOptionId
    );

    return option;
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300',
      active: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
      closed: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    const labels = {
      draft: 'Borrador',
      active: 'Activa',
      closed: 'Cerrada',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badges[status as keyof typeof badges]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };


  const handleToggleContestant = (contestantId: string) => {
    if (selectedContestants.includes(contestantId)) {
      setSelectedContestants(selectedContestants.filter((id) => id !== contestantId));
    } else {
      setSelectedContestants([...selectedContestants, contestantId]);
    }
  };

  const handleAddCustomOption = () => {
    setCustomOptions([...customOptions, '']);
  };

  const handleRemoveCustomOption = (index: number) => {
    if (customOptions.length > 2) {
      setCustomOptions(customOptions.filter((_, i) => i !== index));
    }
  };

  const handleCustomOptionChange = (index: number, value: string) => {
    const newOptions = [...customOptions];
    newOptions[index] = value;
    setCustomOptions(newOptions);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Encuestas</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Gestiona las encuestas de votación</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Crear Encuesta
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Filtrar por temporada
        </label>
        <select
          value={selectedSeason}
          onChange={(e) => setSelectedSeason(e.target.value)}
          className="w-full sm:w-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
        >
          <option value="all">Todas las temporadas</option>
          {seasons.map((season) => (
            <option key={season.id} value={season.id}>
              {season.reality?.name} - Temporada {season.number} ({season.year})
            </option>
          ))}
        </select>
      </div>

      {/* List */}
      <div className="space-y-4 md:space-y-0">
        {polls.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              No hay encuestas registradas. Crea una para comenzar.
            </p>
          </div>
        ) : (
          <>
            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {polls.map((poll) => (
                <div key={poll.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <div className="space-y-3">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white text-lg">{poll.title}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {poll.seasons?.realities?.name} - T{poll.seasons?.number}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {getStatusBadge(poll.status || 'draft')}
                      {poll.is_featured && (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-fuchsia-100 dark:bg-fuchsia-900/30 text-fuchsia-800 dark:text-fuchsia-300">
                          ⭐ Principal
                        </span>
                      )}
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {poll.vote_count || 0} votos
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => handleOpenModal(poll)}
                        className="text-violet-600 dark:text-violet-400 hover:text-violet-900 dark:hover:text-violet-300 text-sm font-medium"
                      >
                        Editar
                      </button>
                      {poll.status === 'active' && (
                        <>
                          <button
                            onClick={() => handleOpenBetrayalModal(poll)}
                            className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 text-sm font-medium"
                          >
                            Traición
                          </button>
                          <button
                            onClick={() => handleOpenDuplicationModal(poll)}
                            className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-900 dark:hover:text-cyan-300 text-sm font-medium"
                          >
                            Duplicar Votos
                          </button>
                          <button
                            onClick={() => handleOpenSavedModal(poll)}
                            className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-900 dark:hover:text-emerald-300 text-sm font-medium"
                          >
                            Salvado
                          </button>
                        </>
                      )}
                      {!poll.is_featured && (
                        <button
                          onClick={() => handleSetFeatured(poll.id)}
                          className="text-fuchsia-600 dark:text-fuchsia-400 hover:text-fuchsia-900 dark:hover:text-fuchsia-300 text-sm font-medium"
                        >
                          Fijar en Landing
                        </button>
                      )}
                      {poll.status === 'active' && (
                        <button
                          onClick={() => handleClose(poll.id)}
                          className="text-orange-600 dark:text-orange-400 hover:text-orange-900 dark:hover:text-orange-300 text-sm font-medium"
                        >
                          Cerrar
                        </button>
                      )}
                      {poll.status === 'closed' && (
                        <button
                          onClick={() => handleActivate(poll.id)}
                          className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 text-sm font-medium"
                        >
                          Reabrir
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(poll.id, poll.title)}
                        className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 text-sm font-medium"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Encuesta
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Votos
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {polls.map((poll) => (
                    <tr key={poll.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{poll.title}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {poll.seasons?.realities?.name} - T{poll.seasons?.number}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusBadge(poll.status || 'draft')}
                          {poll.is_featured && (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-fuchsia-100 dark:bg-fuchsia-900/30 text-fuchsia-800 dark:text-fuchsia-300">
                              ⭐ Principal
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {poll.vote_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleOpenModal(poll)}
                          className="text-violet-600 dark:text-violet-400 hover:text-violet-900 dark:hover:text-violet-300 mr-4"
                        >
                          Editar
                        </button>
                        {poll.status === 'active' && (
                          <>
                            <button
                              onClick={() => handleOpenBetrayalModal(poll)}
                              className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 mr-4"
                            >
                              Traición
                            </button>
                            <button
                              onClick={() => handleOpenDuplicationModal(poll)}
                              className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-900 dark:hover:text-cyan-300 mr-4"
                            >
                              Duplicar Votos
                            </button>
                            <button
                              onClick={() => handleOpenSavedModal(poll)}
                              className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-900 dark:hover:text-emerald-300 mr-4"
                            >
                              Salvado
                            </button>
                          </>
                        )}
                        {!poll.is_featured && (
                          <button
                            onClick={() => handleSetFeatured(poll.id)}
                            className="text-fuchsia-600 dark:text-fuchsia-400 hover:text-fuchsia-900 dark:hover:text-fuchsia-300 mr-4"
                          >
                            Fijar en Landing
                          </button>
                        )}
                        {poll.status === 'active' && (
                          <button
                            onClick={() => handleClose(poll.id)}
                            className="text-orange-600 dark:text-orange-400 hover:text-orange-900 dark:hover:text-orange-300 mr-4"
                          >
                            Cerrar
                          </button>
                        )}
                        {poll.status === 'closed' && (
                          <button
                            onClick={() => handleActivate(poll.id)}
                            className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 mr-4"
                          >
                            Reabrir
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(poll.id, poll.title)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
              onClick={handleCloseModal}
            />

            {/* Modal Content */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[80vh] overflow-y-auto">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    {editingPoll ? 'Editar Encuesta' : 'Nueva Encuesta'}
                  </h3>

                  {/* Error Message in Modal */}
                  {error && (
                    <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
                      {error}
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Título *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Descripción
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      />
                    </div>

                    {/* Reality */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Reality *
                      </label>
                      <select
                        required
                        value={modalRealityId}
                        onChange={(e) => {
                          setModalRealityId(e.target.value);
                          setFormData({ ...formData, season_id: '' }); // Reset season when reality changes
                        }}
                        disabled={!!editingPoll}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="">Selecciona un reality</option>
                        {realities.map((reality) => (
                          <option key={reality.id} value={reality.id}>
                            {reality.name}
                          </option>
                        ))}
                      </select>
                      {editingPoll && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          No se puede cambiar el reality al editar
                        </p>
                      )}
                    </div>

                    {/* Season */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Temporada *
                      </label>
                      <select
                        required
                        value={formData.season_id}
                        onChange={(e) => setFormData({ ...formData, season_id: e.target.value })}
                        disabled={!!editingPoll || !modalRealityId}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="">
                          {!modalRealityId ? 'Selecciona un reality primero' : 'Selecciona una temporada'}
                        </option>
                        {getModalSeasons().map((season) => (
                          <option key={season.id} value={season.id}>
                            Temporada {season.number} ({season.year})
                          </option>
                        ))}
                      </select>
                      {editingPoll && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          No se puede cambiar la temporada al editar
                        </p>
                      )}
                    </div>

                    {/* Vote Mode */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Modo de Voto *
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            checked={voteMode === 'single'}
                            onChange={() => setVoteMode('single')}
                            disabled={!!editingPoll}
                            className="mr-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Voto único (1 opción)</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            checked={voteMode === 'points'}
                            onChange={() => {
                              setVoteMode('points');
                              setDailyReset(true);
                            }}
                            disabled={!!editingPoll}
                            className="mr-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Distribución de puntos</span>
                        </label>
                      </div>
                      {editingPoll && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          No se puede cambiar el modo de voto al editar
                        </p>
                      )}
                    </div>

                    {/* Points to Distribute */}
                    {voteMode === 'points' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Total de puntos a distribuir *
                        </label>
                        <input
                          type="number"
                          required
                          min="2"
                          value={pointsToDistribute}
                          onChange={(e) => setPointsToDistribute(parseInt(e.target.value) || 10)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        />
                      </div>
                    )}

                    {/* Daily Reset - Available for both modes */}
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={dailyReset}
                          onChange={(e) => setDailyReset(e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Resetear puntos diariamente (a las 00:00)
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                        {voteMode === 'points'
                          ? 'Los usuarios recuperarán sus puntos disponibles cada día'
                          : 'Los usuarios podrán votar nuevamente cada día (votos acumulativos)'}
                      </p>
                    </div>

                    {/* Timer */}
                    <div>
                      <label className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          checked={enableTimer}
                          onChange={(e) => {
                            setEnableTimer(e.target.checked);
                            // Si se desmarca, limpiar la fecha
                            if (!e.target.checked) {
                              setFormData({ ...formData, end_date: '' });
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Activar cuenta regresiva
                        </span>
                      </label>
                      {enableTimer && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Fecha y hora de cierre *
                          </label>
                          <input
                            type="datetime-local"
                            required={enableTimer}
                            value={formData.end_date}
                            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                          />
                        </div>
                      )}
                    </div>

                    {/* Options */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Opciones de la encuesta *
                      </label>

                      <div className="mb-3">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            checked={useContestants}
                            onChange={() => setUseContestants(true)}
                            disabled={!!editingPoll}
                            className="mr-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Usar concursantes</span>
                        </label>
                        <label className="flex items-center mt-2">
                          <input
                            type="radio"
                            checked={!useContestants}
                            onChange={() => setUseContestants(false)}
                            disabled={!!editingPoll}
                            className="mr-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Opciones personalizadas</span>
                        </label>
                        {editingPoll && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            No se puede cambiar el tipo de opciones al editar
                          </p>
                        )}
                      </div>

                      {useContestants ? (
                        <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                          {contestants.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              No hay concursantes disponibles en esta temporada
                            </p>
                          ) : (
                            contestants.map((contestant) => (
                              <label key={contestant.id} className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={selectedContestants.includes(contestant.id)}
                                  onChange={() => handleToggleContestant(contestant.id)}
                                  className="mr-2"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  {contestant.full_name}
                                </span>
                              </label>
                            ))
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {customOptions.map((option, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => handleCustomOptionChange(index, e.target.value)}
                                placeholder={`Opción ${index + 1}`}
                                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                              />
                              {customOptions.length > 2 && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveCustomOption(index)}
                                  className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={handleAddCustomOption}
                            className="text-sm text-violet-600 dark:text-violet-400 hover:text-violet-900 dark:hover:text-violet-300"
                          >
                            + Agregar opción
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-violet-600 text-base font-medium text-white hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                  >
                    {editingPoll ? 'Actualizar' : 'Crear'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="mt-3 w-full sm:mt-0 sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
              onClick={() => setDeleteConfirmModal({ isOpen: false, pollId: null, pollTitle: '' })}
            />

            {/* Modal Content */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Eliminar encuesta
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        ¿Estás seguro de que quieres eliminar la encuesta <span className="font-semibold">"{deleteConfirmModal.pollTitle}"</span>?
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        Esta acción eliminará permanentemente todos los votos, opciones y configuraciones asociadas. Esta acción no se puede deshacer.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Eliminar
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmModal({ isOpen: false, pollId: null, pollTitle: '' })}
                  className="mt-3 w-full sm:mt-0 sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Saved Modal */}
      {savedModal.isOpen && savedModal.poll && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
              onClick={handleCloseSavedModal}
            />

            {/* Modal Content */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Marcar Salvado
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {savedModal.poll.title}
                    </p>

                    {/* Error Message */}
                    {error && (
                      <div className="mt-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-3 py-2 rounded-lg text-sm">
                        {error}
                      </div>
                    )}

                    <div className="mt-4 space-y-4">
                      {/* Select option to save */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Selecciona el nominado:
                        </label>
                        <select
                          value={savedModal.selectedOptionId}
                          onChange={(e) => setSavedModal(prev => ({ ...prev, selectedOptionId: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        >
                          <option value="">Selecciona un nominado</option>
                          {savedModal.poll.poll_options
                            .filter(opt => opt.contestant_id || opt.text)
                            .map(opt => {
                              const name = opt.contestants?.full_name || opt.text;
                              const isSaved = opt.is_saved;
                              return (
                                <option key={opt.id} value={opt.id}>
                                  {name} {isSaved ? '✓ Salvado' : ''}
                                </option>
                              );
                            })}
                        </select>
                      </div>

                      {/* Show current status if option selected */}
                      {savedModal.selectedOptionId && (() => {
                        const selectedOption = savedModal.poll!.poll_options.find(opt => opt.id === savedModal.selectedOptionId);
                        if (!selectedOption) return null;
                        const name = selectedOption.contestants?.full_name || selectedOption.text;
                        const isSaved = selectedOption.is_saved;

                        return (
                          <div className={`border rounded-lg p-3 ${isSaved ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' : 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700'}`}>
                            <p className={`text-sm font-semibold ${isSaved ? 'text-emerald-800 dark:text-emerald-300' : 'text-gray-800 dark:text-gray-300'}`}>
                              Estado actual: {isSaved ? '✓ Salvado' : 'No salvado'}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              {isSaved
                                ? `${name} aparecerá con badge de salvado y no se podrá votar por él/ella`
                                : `${name} se marcará como salvado y se deshabilitará la votación`
                              }
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  onClick={handleSavedSubmit}
                  disabled={!savedModal.selectedOptionId || savedModal.isSubmitting}
                  className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-emerald-600 text-base font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savedModal.isSubmitting ? 'Procesando...' : (() => {
                    const selectedOption = savedModal.poll?.poll_options.find(opt => opt.id === savedModal.selectedOptionId);
                    return selectedOption?.is_saved ? 'Quitar Salvado' : 'Marcar Salvado';
                  })()}
                </button>
                <button
                  type="button"
                  onClick={handleCloseSavedModal}
                  disabled={savedModal.isSubmitting}
                  className="mt-3 w-full sm:mt-0 sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplication Modal */}
      {duplicationModal.isOpen && duplicationModal.poll && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
              onClick={handleCloseDuplicationModal}
            />

            {/* Modal Content */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-cyan-100 dark:bg-cyan-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-cyan-600 dark:text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Duplicar Votos
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {duplicationModal.poll.title}
                    </p>

                    {/* Error Message */}
                    {error && (
                      <div className="mt-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-3 py-2 rounded-lg text-sm">
                        {error}
                      </div>
                    )}

                    <div className="mt-4 space-y-4">
                      {/* Select source contestant */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Candidato origen (cuyos votos se duplicarán):
                        </label>
                        <select
                          value={duplicationModal.sourceOptionId}
                          onChange={(e) => setDuplicationModal(prev => ({ ...prev, sourceOptionId: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        >
                          <option value="">Selecciona un candidato</option>
                          {duplicationModal.poll.poll_options
                            .filter(opt => opt.contestant_id)
                            .map(opt => (
                              <option key={opt.id} value={opt.id}>
                                {opt.contestants?.full_name}
                              </option>
                            ))}
                        </select>
                      </div>

                      {/* Show vote stats if source selected */}
                      {duplicationModal.sourceOptionId && (() => {
                        const sourceOption = duplicationModal.poll!.poll_options.find(opt => opt.id === duplicationModal.sourceOptionId);
                        if (!sourceOption) return null;

                        return (
                          <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg p-3">
                            <p className="text-sm text-violet-800 dark:text-violet-300">
                              Votos a duplicar: <span className="font-semibold">{duplicationModal.poll.vote_count || 0} votos</span>
                            </p>
                            <p className="text-xs text-violet-600 dark:text-violet-400 mt-1">
                              Estos votos se copiarán al nuevo candidato (el origen permanecerá activo)
                            </p>
                          </div>
                        );
                      })()}

                      {/* Select target contestant */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Candidato destino (recibirá los votos):
                        </label>
                        <select
                          value={duplicationModal.targetContestantId}
                          onChange={(e) => setDuplicationModal(prev => ({ ...prev, targetContestantId: e.target.value }))}
                          disabled={!duplicationModal.sourceOptionId}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="">Selecciona el destino</option>
                          {getAvailableContestantsForDuplication().map(contestant => (
                            <option key={contestant.id} value={contestant.id}>
                              {contestant.full_name}
                            </option>
                          ))}
                        </select>
                        {!duplicationModal.sourceOptionId && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Primero selecciona el candidato origen
                          </p>
                        )}
                      </div>

                      {/* Reason (optional) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Razón (opcional):
                        </label>
                        <input
                          type="text"
                          value={duplicationModal.reason}
                          onChange={(e) => setDuplicationModal(prev => ({ ...prev, reason: e.target.value }))}
                          placeholder="Ej: Duplicación - Semana 3"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        />
                      </div>

                      {/* Confirmation */}
                      {duplicationModal.sourceOptionId && duplicationModal.targetContestantId && (
                        <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg p-3">
                          <p className="text-sm text-cyan-800 dark:text-cyan-300 font-semibold">
                            ℹ️ Confirmación
                          </p>
                          <p className="text-xs text-cyan-700 dark:text-cyan-400 mt-1">
                            Los votos de{' '}
                            <span className="font-semibold">
                              {duplicationModal.poll.poll_options.find(opt => opt.id === duplicationModal.sourceOptionId)?.contestants?.full_name}
                            </span>
                            {' '}se copiarán a{' '}
                            <span className="font-semibold">
                              {contestants.find(c => c.id === duplicationModal.targetContestantId)?.full_name}
                            </span>
                            . El candidato origen permanecerá activo y con sus votos.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  onClick={handleDuplicationSubmit}
                  disabled={!duplicationModal.sourceOptionId || !duplicationModal.targetContestantId || duplicationModal.isSubmitting}
                  className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-cyan-600 text-base font-medium text-white hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {duplicationModal.isSubmitting ? 'Procesando...' : 'Confirmar Duplicación'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseDuplicationModal}
                  disabled={duplicationModal.isSubmitting}
                  className="mt-3 w-full sm:mt-0 sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Betrayal Modal */}
      {betrayalModal.isOpen && betrayalModal.poll && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
              onClick={handleCloseBetrayalModal}
            />

            {/* Modal Content */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Función Traición
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {betrayalModal.poll.title}
                    </p>

                    {/* Error Message */}
                    {error && (
                      <div className="mt-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-3 py-2 rounded-lg text-sm">
                        {error}
                      </div>
                    )}

                    <div className="mt-4 space-y-4">
                      {/* Select contestant to save */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Nominado que se salva:
                        </label>
                        <select
                          value={betrayalModal.selectedOptionId}
                          onChange={(e) => setBetrayalModal(prev => ({ ...prev, selectedOptionId: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        >
                          <option value="">Selecciona un nominado</option>
                          {betrayalModal.poll.poll_options
                            .filter(opt => opt.contestant_id)
                            .map(opt => (
                              <option key={opt.id} value={opt.id}>
                                {opt.contestants?.full_name}
                              </option>
                            ))}
                        </select>
                      </div>

                      {/* Show vote stats if option selected */}
                      {betrayalModal.selectedOptionId && getSelectedOptionStats() && (
                        <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg p-3">
                          <p className="text-sm text-violet-800 dark:text-violet-300">
                            Votos actuales: <span className="font-semibold">{betrayalModal.poll.vote_count || 0} votos</span>
                          </p>
                          <p className="text-xs text-violet-600 dark:text-violet-400 mt-1">
                            Estos votos se transferirán al nuevo nominado
                          </p>
                        </div>
                      )}

                      {/* Select new contestant */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Nuevo nominado:
                        </label>
                        <select
                          value={betrayalModal.newContestantId}
                          onChange={(e) => setBetrayalModal(prev => ({ ...prev, newContestantId: e.target.value }))}
                          disabled={!betrayalModal.selectedOptionId}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="">Selecciona el reemplazo</option>
                          {getAvailableContestants().map(contestant => (
                            <option key={contestant.id} value={contestant.id}>
                              {contestant.full_name}
                            </option>
                          ))}
                        </select>
                        {!betrayalModal.selectedOptionId && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Primero selecciona quién se salva
                          </p>
                        )}
                      </div>

                      {/* Reason (optional) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Razón (opcional):
                        </label>
                        <input
                          type="text"
                          value={betrayalModal.reason}
                          onChange={(e) => setBetrayalModal(prev => ({ ...prev, reason: e.target.value }))}
                          placeholder="Ej: Traición - Semana 3"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        />
                      </div>

                      {/* Warning */}
                      {betrayalModal.selectedOptionId && betrayalModal.newContestantId && (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                          <p className="text-sm text-yellow-800 dark:text-yellow-300 font-semibold">
                            ⚠️ Confirmación
                          </p>
                          <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                            Los votos de{' '}
                            <span className="font-semibold">
                              {betrayalModal.poll.poll_options.find(opt => opt.id === betrayalModal.selectedOptionId)?.contestants?.full_name}
                            </span>
                            {' '}se transferirán a{' '}
                            <span className="font-semibold">
                              {contestants.find(c => c.id === betrayalModal.newContestantId)?.full_name}
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  onClick={handleBetrayalSubmit}
                  disabled={!betrayalModal.selectedOptionId || !betrayalModal.newContestantId || betrayalModal.isSubmitting}
                  className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-yellow-600 text-base font-medium text-white hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {betrayalModal.isSubmitting ? 'Procesando...' : 'Confirmar Traición'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseBetrayalModal}
                  disabled={betrayalModal.isSubmitting}
                  className="mt-3 w-full sm:mt-0 sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
